import { useDarkMode } from "@/hooks/useDarkMode";
import { cn } from "@/lib/utils";
import { bucketUrlPrefix } from "common/config";
import { AccessibilityCategory } from "common/types";
import { useEffect, useState } from "react";
import AccessibilitySubItem from "./AccessibilitySubItem";
import FlagVenueAccessibility from "@/features/flag-report/FlagVenueAccessibility";
import { useAuth } from "common/hooks";
import { useAtom } from "jotai";
import {
  actionToPerformAfterLoginAtom,
  actionToPerformValueAfterLoginAtom,
} from "@/store/atoms";
import AnalyticsService from "@/services/analytics";

type AccessibilityItemProps = {
  category: AccessibilityCategory;
  fullSize?: boolean;
  venueId: string;
  venueName: string;
  businessId: string;
  onLogin: () => void;
  venueDetails: any;
  categories: AccessibilityCategory[];
  lastUpdated: string;
};

function AccessibilityItem({
  category,
  fullSize,
  venueId,
  venueName,
  businessId,
  onLogin,
  venueDetails,
  categories,
  lastUpdated,
}: AccessibilityItemProps) {
  const isDark = useDarkMode();
  const [isFlagVenueOpen, setIsFlagVenueOpen] = useState(false);
  const [selectedTagForFlag, setSelectedTagForFlag] = useState<any>(null);
  const { user, isLoggedIn } = useAuth();
  const [actionToPerformAfterLogin, setActionToPerformAfterLogin] = useAtom(
    actionToPerformAfterLoginAtom
  );
  const [actionToPerformValueAfterLogin, setActionToPerformValueAfterLogin] =
    useAtom(actionToPerformValueAfterLoginAtom);

  useEffect(() => {
    if (isLoggedIn && user && actionToPerformAfterLogin) {
      if (actionToPerformAfterLogin === "flagAccessibility") {
        setSelectedTagForFlag(actionToPerformValueAfterLogin);
        setIsFlagVenueOpen(true);
      }
      setActionToPerformAfterLogin("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionToPerformAfterLogin, user, isLoggedIn]);

  const handleFlagVenueAccessibilityClose = () => {
    AnalyticsService.trackGAEventHandler("flagAccessibilityReportClose");
    AnalyticsService.trackMx("flagAccessibilityReportClose", {});
    setIsFlagVenueOpen(false);
  };

  return (
    <div className="">
      <div
        role="button"
        className="flex justify-between items-center mb-2 px-2 md:px-0 md:mr-2"
      >
        <div className="flex items-center gap-6">
          <img
            className="w-[24px] h-[24px]"
            src={
              bucketUrlPrefix +
              (isDark ? category.catImageDark : category.catImage)
            }
            alt=""
            aria-hidden="true"
          />
          <h1
            className="desktop-h3-small-semi-bold text-primary-800 dark:text-primary-150"
            tabIndex={0}
          >
            {category.catName}
          </h1>
        </div>
      </div>
      <section className="px-0 py-4 " role="none">
        <div className={cn("grid grid-cols-1 gap-2")}>
          {category.tags.map((tag, index) => {
            let reviewPhotos = tag?.reviewPhotos;
            //console.log(category.catName, tag.qtagsAll);
            if (category.catId === 96) {
              //Indoors VSH
              //Find Indoors
              const indoor = categories.find((c) => c.catId === 58);
              if (indoor) {
                if (indoor.tags[index]) {
                  reviewPhotos = reviewPhotos.concat(
                    indoor.tags[index]?.reviewPhotos
                  );
                }
              }
            } else if (category.catId === 216) {
              //Outdoors VSH
              //Find Outdoors
              const indoor = categories.find((c) => c.catId === 172);
              if (indoor) {
                if (indoor.tags[index]?.reviewPhotos) {
                  reviewPhotos = reviewPhotos.concat(
                    indoor.tags[index]?.reviewPhotos
                  );
                }
              }
            }
            return (
              <div key={index} className="">
                <AccessibilitySubItem
                  venueId={venueId}
                  qtags={tag.qtags}
                  qtagsAll={tag.qtagsAll}
                  sections={category.cat_display_sections}
                  reviewPhotos={reviewPhotos}
                  fullSize={fullSize}
                  venueDetails={venueDetails}
                  categories={categories}
                  onFlag={(mainTag) => {
                    if (!user || !isLoggedIn) {
                      // Set the method in the Atom
                      console.log("setActionToPerformAfterLogin in the Atom");
                      setActionToPerformAfterLogin("flagAccessibility");
                      setActionToPerformValueAfterLogin(mainTag);

                      onLogin();
                      return;
                    }
                    setSelectedTagForFlag(mainTag);
                    setIsFlagVenueOpen(true);
                  }}
                  lastUpdated={lastUpdated}
                />
              </div>
            );
          })}
        </div>
      </section>
      <FlagVenueAccessibility
        venueId={businessId}
        venueName={venueName}
        mainTag={selectedTagForFlag}
        catName={category.catName}
        isOpen={isFlagVenueOpen}
        onClose={() => handleFlagVenueAccessibilityClose()}
        venueDetails={venueDetails}
      />
    </div>
  );
}

export default AccessibilityItem;
